import { loadScript } from 'core/Utils'
import { GoogleMapDirectionType, GoogleMapPlace, Place } from 'models'
import { useCallback } from 'react'

const autocompleteService = {
  current: null as google.maps.places.AutocompleteService | null,
}

const directionService = {
  current: null as google.maps.DirectionsService | null,
}

export type GetPlacePredictionsCallback = (
  results?: readonly GoogleMapPlace[],
) => void

export type CalculateDistanceCallBack = (
  result?: GoogleMapDirectionType,
) => void

export const useGoogleMap = (): {
  loadGoogleMapScript: () => void
  fillAddressComponents: (place: Place) => Promise<Place>
  getPlacePredictions: (
    input: string,
    callback: GetPlacePredictionsCallback,
  ) => void
  calculateDistance: (
    origin: Place,
    destination: Place,
    callback: CalculateDistanceCallBack,
  ) => void
  verifyZipMatchesCityState: (
    zip?: string,
    city?: string,
    state?: string,
  ) => Promise<boolean>
} => {
  const loadGoogleMapScript = useCallback(() => {
    if (
      typeof window !== 'undefined' &&
      !document.querySelector('#google-maps')
    ) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GG_MAP_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps',
      )
    }
  }, [])

  const fillAddressComponents = useCallback(
    async (place: Place): Promise<Place> => {
      return new Promise<Place>((resolve, reject) => {
        if (!place.place_id) return resolve({} as Place)

        const geocoder = new (window as any).google.maps.Geocoder()
        geocoder.geocode({ placeId: place.place_id }, (results: any[]) => {
          if (results === null || results.length === 0) {
            return resolve({} as Place)
          }

          const getPart = (name: string): string => {
            return results[0]?.address_components.find((e: any) =>
              e.types.includes(name),
            )?.long_name
          }

          const city = getPart('locality')
          const state = getPart('administrative_area_level_1')
          const subpremise = getPart('subpremise') ?? ''
          const street = `${getPart('street_number') ?? ''} ${
            getPart('route') ?? ''
          } ${subpremise ? `#${subpremise}` : ''}`.trim()
          const postalCode = getPart('postal_code')

          return resolve({
            description: place.description,
            place_id: place.place_id,
            city,
            state,
            street,
            zip_code: postalCode,
          })
        })
      })
    },
    [],
  )

  const getPlacePredictions = useCallback(
    (input: string, callback: GetPlacePredictionsCallback) => {
      if (!autocompleteService.current) {
        autocompleteService.current = new (
          window as any
        ).google.maps.places.AutocompleteService()
      }
      if (!autocompleteService.current) return
      autocompleteService.current?.getPlacePredictions({ input }, callback)
    },
    [],
  )

  const calculateDistance = useCallback(
    (
      origin: Place,
      destination: Place,
      callback: CalculateDistanceCallBack,
    ) => {
      if (!directionService.current) {
        directionService.current = new (
          window as any
        ).google.maps.DirectionsService()
      }
      if (!directionService.current) return
      directionService.current?.route(
        {
          origin: { placeId: origin.place_id },
          destination: destination.place_id
            ? { placeId: destination.place_id }
            : destination.description,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        callback,
      )
    },
    [],
  )

  // Reverse-geocodes the zip via Google and checks its city/state against the given ones.
  const verifyZipMatchesCityState = useCallback(
    (zip?: string, city?: string, state?: string): Promise<boolean> => {
      return new Promise((resolve) => {
        const normalize = (s?: string) => s?.trim().toLowerCase()
        const geocoder = new (window as any).google.maps.Geocoder()
        geocoder.geocode(
          { componentRestrictions: { country: 'US', postalCode: zip } },
          (results: any[], status: string) => {
            // ZERO_RESULTS means the zip code itself doesn't exist, so it can't
            // match any City/State - that's an invalid combination, not a lookup failure.
            if (status === 'ZERO_RESULTS') {
              return resolve(false)
            }

            // Fail-open on infra errors (network, quota, etc.) so a Google
            // outage never blocks checkout.
            if (status !== 'OK' || !results?.length) {
              return resolve(true)
            }

            const getPart = (name: string): string | undefined =>
              results[0]?.address_components.find((e: any) =>
                e.types.includes(name),
              )?.long_name

            const zipCity =
              getPart('locality') ??
              getPart('sublocality') ??
              getPart('postal_town')
            const zipState = getPart('administrative_area_level_1')

            const cityMatches =
              !city || !zipCity || normalize(zipCity) === normalize(city)
            const stateMatches =
              !state || !zipState || normalize(zipState) === normalize(state)

            resolve(cityMatches && stateMatches)
          },
        )
      })
    },
    [],
  )

  return {
    loadGoogleMapScript,
    fillAddressComponents,
    getPlacePredictions,
    calculateDistance,
    verifyZipMatchesCityState,
  }
}
